import { useEffect, useState } from 'react';
import { warehouseApi, type WmPickListResponse, type WmPutawayTask } from '@/api/warehouse.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { usePermissions } from '@/hooks/usePermissions';
import { showApiError } from '@/utils/apiError';
import { Link } from 'react-router-dom';
import { notify } from '@/core/toast/notify';
import { useProductOptions } from '@/hooks/useProductOptions';

export function WmsMobilePage() {
  const { hasAny } = usePermissions();
  const canReceive = hasAny('WAREHOUSE_RECEIVE', 'WAREHOUSE_ADMIN');
  const canPick = hasAny('WAREHOUSE_PICK', 'WAREHOUSE_ADMIN');
  const { getLabel: getProductLabel } = useProductOptions();
  const [putaway, setPutaway] = useState<WmPutawayTask[]>([]);
  const [picks, setPicks] = useState<WmPickListResponse[]>([]);
  const [barcode, setBarcode] = useState('');
  const [binId, setBinId] = useState('');
  const [scanning, setScanning] = useState(false);

  const reload = () => {
    Promise.all([warehouseApi.listOpenPutaway(), warehouseApi.listOpenPicks()])
      .then(([p, pk]) => {
        setPutaway(p);
        setPicks(pk);
      })
      .catch((e) => showApiError(e, 'Failed to load tasks'));
  };

  useEffect(() => {
    reload();
  }, []);

  const scan = async () => {
    if (!barcode.trim()) return;
    setScanning(true);
    try {
      await warehouseApi.scan({ barcodeValue: barcode.trim(), operation: 'MOBILE_SCAN' });
      notify.success('Scan recorded');
      setBarcode('');
      reload();
    } catch (e) {
      showApiError(e, 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const confirmPutaway = async (taskId: number) => {
    try {
      await warehouseApi.confirmPutaway(taskId, binId ? Number(binId) : undefined);
      notify.success('Putaway confirmed');
      reload();
    } catch (e) {
      showApiError(e, 'Putaway failed');
    }
  };

  const confirmPick = async (taskId: number, qty: number) => {
    try {
      await warehouseApi.confirmPick(taskId, { pickedQuantity: qty });
      notify.success('Pick confirmed');
      reload();
    } catch (e) {
      showApiError(e, 'Pick failed');
    }
  };

  return (
    <div className="mx-auto max-w-lg p-4">
      <PageHeader
        title="WMS Mobile"
        description="Floor tasks for putaway and picking"
        breadcrumbs={[
          { label: 'Warehouse', to: '/warehouse' },
          { label: 'Mobile' },
        ]}
      />
      <Link to="/warehouse/operations" className="mb-4 block text-sm text-primary-600">
        Desktop floor operations
      </Link>
      {(canReceive || canPick) && (
        <div className="card mb-4 space-y-3 p-4">
          <Input label="Barcode" value={barcode} onChange={(e) => setBarcode(e.target.value)} />
          {canReceive && (
            <Input
              label="Target bin ID (putaway)"
              value={binId}
              onChange={(e) => setBinId(e.target.value)}
            />
          )}
          <Button className="w-full py-4 text-lg" loading={scanning} onClick={() => void scan()}>
            Scan
          </Button>
        </div>
      )}
      <section className="mb-6">
        <h2 className="mb-2 text-sm font-semibold uppercase text-slate-500">
          Open putaway ({putaway.length})
        </h2>
        <ul className="space-y-2">
          {putaway.map((t) => (
            <li key={t.id} className="card space-y-2 p-3 text-sm">
              <p>
                #{t.taskCode} · {getProductLabel(t.productId, true)} · qty {t.quantity}
              </p>
              {canReceive ? (
                <Button className="w-full" onClick={() => void confirmPutaway(t.id)}>
                  Confirm putaway
                </Button>
              ) : (
                <p className="text-xs text-slate-500">Receive permission required</p>
              )}
            </li>
          ))}
        </ul>
      </section>
      <section>
        <h2 className="mb-2 text-sm font-semibold uppercase text-slate-500">
          Open picks ({picks.length})
        </h2>
        <ul className="space-y-2">
          {picks.flatMap((p) =>
            p.tasks
              .filter((t) => t.statusCode === 'OPEN')
              .map((t) => (
                <li key={t.id} className="card space-y-2 p-3 text-sm">
                  <p>
                    {p.pickCode} · {getProductLabel(t.productId, true)} · qty {t.quantity}
                  </p>
                  {canPick ? (
                    <Button className="w-full" onClick={() => void confirmPick(t.id, t.quantity)}>
                      Confirm pick
                    </Button>
                  ) : (
                    <p className="text-xs text-slate-500">Pick permission required</p>
                  )}
                </li>
              )),
          )}
        </ul>
        <Link to="/warehouse/picks" className="mt-2 block text-sm text-primary-600">
          Full pick / pack / dispatch
        </Link>
      </section>
    </div>
  );
}
